import React from "react";
import { fetchQuery, graphql } from "react-relay";

import { faHashtag, faLock } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { throttle } from "lodash";

import { environment } from "~/models";
import AsyncMultiSelector, {
	type AsyncMultiSelectorProps,
	type MultiSelectorOption,
} from "~/ui/selectors/AsyncMultiSelector";

import type {
	ChannelMultiSelectorQuery$data,
	ChannelMultiSelectorQuery as ChannelMultiSelectorQueryType,
} from "~/__generated__/ChannelMultiSelectorQuery.graphql";

const ChannelMultiSelectorQuery = graphql`
	query ChannelMultiSelectorQuery($search: String) {
		viewer {
			channels(search: $search, excludePersonal: true, orderBy: NAME_ASC) {
				id
				name
				isPrivate
			}
		}
	}
`;

export const convertChannelsToOptions = (channels: ChannelMultiSelectorQuery$data["viewer"]["channels"]) => {
	if (!channels) {
		return [];
	}

	return channels.map(channel => ({
		label: channel.name,
		value: channel.id,
		icon: channel.isPrivate ? faLock : faHashtag,
	}));
};

export const convertOptionsToChannels = (options: MultiSelectorOption[]) => {
	return options.map(option => ({
		id: option.value,
		name: option.label,
		isPrivate: option.icon === faLock,
	}));
};

type ChannelMultiSelectorProps = Pick<
	AsyncMultiSelectorProps<MultiSelectorOption>,
	"className" | "onChange" | "isDisabled" | "value" | "condensed" | "placeholder" | "menuAlign"
>;

const ChannelMultiSelector: React.FC<ChannelMultiSelectorProps> = ({ ...rest }) => {
	const loadOptions = React.useCallback((search: string, callback: (options: MultiSelectorOption[]) => void) => {
		fetchQuery<ChannelMultiSelectorQueryType>(environment, ChannelMultiSelectorQuery, { search })
			.toPromise()
			.then(result => {
				if (!result) {
					return;
				}

				const channels = convertChannelsToOptions(result.viewer.channels);

				callback(channels);
			});
	}, []);

	const throttledLoadOptions = React.useMemo(() => throttle(loadOptions, 250), [loadOptions]);

	React.useEffect(() => {
		return () => throttledLoadOptions.cancel();
	}, [throttledLoadOptions]);

	return (
		<AsyncMultiSelector<MultiSelectorOption>
			placeholder="Select channels (Optional)"
			condensed
			menuAlign="end"
			{...rest}
			loadOptions={throttledLoadOptions}
			isClearable={false}
		/>
	);
};

export default ChannelMultiSelector;
